/* eslint-disable */
import React, { useRef, useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import PompiLogo from '../../assets/PompiImage.svg';
import ArrowRight from '../../assets/icons/arrow-right.svg';
import AuthHeader from '../../components/AuthHeader';
import AuthInput from '../../components/AuthInput';
import AuthButton from '../../components/AuthButton';
import PhoneInput, { DEFAULT_COUNTRY } from '../../components/PhoneInput';
import { colors, fonts } from '../../config/theme';
import { isValidEmail } from '../../utility/validation';
import { sendSignupOtp } from '../../services/auth-service';
import Toast from 'react-native-toast-message';
import { Trans, useTranslation } from 'react-i18next';

const CreateAccount = ({ navigation }) => {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const passwordRef = useRef(null);

  const validate = () => {
    const next = {};
    if (!name.trim()) {
      next.name = t(`root.Auth.nameRequired`);
    }
    if (!email.trim()) {
      next.email = t(`root.Auth.emailRequired`);
    } else if (!isValidEmail(email)) {
      next.email = t(`root.Auth.emailInvalid`);
    }
    if (!phone.trim()) {
      next.phone = t(`root.Auth.phoneRequired`);
    }
    if (!password) {
      next.password = t(`root.Auth.passwordRequired`);
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Clear a field's error as soon as the user starts fixing it.
  const onChange = (field, setter) => value => {
    setter(value);
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCreateAccount = async () => {
    if (!validate()) {
      return;
    }
    setSubmitting(true);
    try {
      // e.g. 31612345678 (country code + number, digits only, no '+', leading 0 removed)
      const fullPhone = `${String(country.dial).replace(/\D/g, '')}${phone
        .replace(/\D/g, '')
        .replace(/^0+/, '')}`;

      const body = {
        email: email.trim(),
        name: name.trim(),
        phone_number: fullPhone,
        password,
      };
      // The account is only created once the emailed code checks out
      // (VerifyOtp, mode 'signup').
      await sendSignupOtp(body.email, body.phone_number);
      navigation.navigate('VerifyOtp', {
        mode: 'signup',
        email: body.email,
        signup: body,
      });
    } catch (err) {
      console.log('handleCreateAccount error', err);
      Toast.show({
        type: 'error',
        text1: err.message || t(`root.Toast.Error.SignupFailed`),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigation.goBack();
  };
  return (
    <View style={styles.screen}>
      <AuthHeader title={t(`root.CreateAccount.title`)} onBack={handleBack} />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          style={styles.flex}
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: insets.bottom + 24 },
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <View style={styles.logoWrapper}>
            <PompiLogo width={140} height={134} />
          </View>

          <View style={styles.form}>
            <AuthInput
              label={t(`root.CreateAccount.name`)}
              placeholder={t(`root.CreateAccount.namePlaceholder`)}
              value={name}
              onChangeText={onChange('name', setName)}
              error={errors.name}
              autoCapitalize="words"
              autoComplete="name"
              textContentType="name"
              returnKeyType="next"
              submitBehavior="submit"
              onSubmitEditing={() => emailRef.current?.focus()}
            />
            <AuthInput
              ref={emailRef}
              label={t(`root.CreateAccount.email`)}
              placeholder={t(`root.CreateAccount.emailPlaceholder`)}
              value={email}
              onChangeText={onChange('email', setEmail)}
              error={errors.email}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="email"
              textContentType="username"
              returnKeyType="next"
              submitBehavior="submit"
              onSubmitEditing={() => phoneRef.current?.focus()}
            />
            <PhoneInput
              ref={phoneRef}
              label={t(`root.CreateAccount.phone`)}
              placeholder={t(`root.CreateAccount.phonePlaceholder`)}
              value={phone}
              onChangeText={onChange('phone', setPhone)}
              error={errors.phone}
              country={country}
              onCountryChange={setCountry}
              autoComplete="tel"
              textContentType="telephoneNumber"
              returnKeyType="next"
              submitBehavior="submit"
              onSubmitEditing={() => passwordRef.current?.focus()}
            />
            <AuthInput
              ref={passwordRef}
              secure
              label={t(`root.CreateAccount.password`)}
              placeholder={t(`root.CreateAccount.passwordPlaceholder`)}
              value={password}
              onChangeText={onChange('password', setPassword)}
              error={errors.password}
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="new-password"
              textContentType="newPassword"
              returnKeyType="done"
              onSubmitEditing={handleCreateAccount}
            />

            <Text style={styles.terms}>
              <Trans
                i18nKey="root.CreateAccount.terms"
                components={{
                  b: <Text style={styles.termsStrong} />,
                  terms: <Text style={[styles.termsStrong, styles.termsLink]} />,
                  privacy: <Text style={[styles.termsStrong, styles.termsLink]} />,
                }}
              />
            </Text>

            <AuthButton
              variant="primary"
              title={t(`root.CreateAccount.submit`)}
              iconRight={<ArrowRight width={16} height={16} color={colors.onPrimary} />}
              loading={submitting}
              onPress={handleCreateAccount}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.surface,
  },
  flex: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  logoWrapper: {
    alignItems: 'center',
    paddingTop: 16,
    paddingBottom: 8,
  },
  form: {
    paddingHorizontal: 24,
    gap: 16,
  },
  terms: {
    marginTop: 8,
    paddingHorizontal: 8,
    fontFamily: fonts.regular,
    fontSize: 11.5,
    lineHeight: 18,
    color: colors.textMuted,
    textAlign: 'center',
  },
  termsStrong: {
    fontFamily: fonts.bold,
    color: colors.textEmphasis,
  },
  termsLink: {
    textDecorationLine: 'underline',
  },
});

export default CreateAccount;